import { useEffect, useState } from 'react';

import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { apolloClient } from '../../App';

import { PAGES } from '../../constants/pages';

import {
    GetStockTransferQuery,
    StockTransferState,
    useUpdateStockTransferMutation,
    useGetStockTransferQuery,
    useAbortStockTransfersMutation,
} from 'data/__generated__';

import { ActionsAndSave, PageActionsMenu, type PageActionGroup } from 'components/PageHeader';

import { STOCK_TRANSFERS_UPDATED_SUBSCRIPTION } from 'data/subscriptions/stockTransfer';

import { StatesSection } from 'pages/StockTransfers/StatesSection';

import { ColumnsSelector } from './components/ColumnsSelector';
import { ProductsToTransferTable } from './components/ProductsToTransferTable';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { SectionColumn } from 'components/DetailsView/Section';
import { DetailFormDatePicker } from 'components/DetailsView/DetailFormDatePicker';
import { PageTitle } from 'components/PageTitle';
import { DetailLink } from 'components/DetailsView/DetailLink';

import { stockTransferUpdatedHandler } from './utils/stockTransfersUpdatedHandler';
import { dateFromString, formatDateAsAnniversary, getExtendedFormattedDatetime } from '../../helpers/dateTimes';
import { colors } from 'constants/colors';
import { DetailFormCheckbox } from 'components/DetailsView/DetailFormCheckbox';
import { StockTransferFormData, StockTransferFormSchema, defaultStockTransferValues } from './stockTransfer.schema';
import { round } from 'lodash';
import { confirmPopup } from 'components/Popups/Confirm';

type ParamTypes = {
    stockTransferId: string;
};

export const StockTransferDetails = () => {
    const { stockTransferId = '' } = useParams<ParamTypes>();
    const navigate = useNavigate();
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

    const methods = useForm<StockTransferFormData>({
        resolver: zodResolver(StockTransferFormSchema),
        defaultValues: defaultStockTransferValues,
    });

    const {
        formState: { isDirty },
        handleSubmit,
        reset,
        watch,
        control,
    } = methods;

    const {
        loading: stockTransferLoading,
        data: stockTransferData,
        error: stockTransferError,
    } = useGetStockTransferQuery({
        variables: { stockTransferId },
    });

    const [updateStockTransfer, { loading: updateLoading }] = useUpdateStockTransferMutation();
    const [abortStockTransfers, { loading: abortLoading }] = useAbortStockTransfersMutation();

    // Mettre à jour les valeurs par défaut quand les données sont chargées
    useEffect(() => {
        if (stockTransferData?.stockTransfer) {
            const stockTransfer = stockTransferData.stockTransfer;
            reset({
                transferDate: stockTransfer.transferDate,
                originColumnId: stockTransfer.originColumnId,
                destinationColumnId: stockTransfer.destinationColumnId,
                shouldUpdateStockImmediately: stockTransfer.shouldUpdateStockImmediately,
                products: stockTransfer.products.map(
                    (product: GetStockTransferQuery['stockTransfer']['products'][number]) => {
                        const conditionning = product.conditionning || 0;
                        const packagesPerBatch = product.packagesPerBatch || 0;
                        const quantity = product.quantity || 0;
                        return {
                            productId: product.productId,
                            quantity,
                            packages: conditionning > 0 ? round(quantity / conditionning, 2) : 0,
                            palette:
                                conditionning > 0 && packagesPerBatch > 0
                                    ? round(quantity / conditionning / packagesPerBatch, 2)
                                    : 0,
                            fullname: product.fullname,
                            conditionning,
                            packagesPerBatch,
                            originStockAvailable: product.originStockAvailable,
                            destinationStockAvailable: product.destinationStockAvailable,
                        };
                    },
                ),
            });
        }
    }, [stockTransferData?.stockTransfer, reset]);

    useEffect(() => {
        const observer = apolloClient.subscribe({
            query: STOCK_TRANSFERS_UPDATED_SUBSCRIPTION,
            variables: { stockTransferId },
        });
        const subscription = observer.subscribe(({ data }) => {
            const { stockTransferUpdated } = data;
            stockTransferUpdatedHandler(stockTransferUpdated, stockTransferId);
            if (stockTransferUpdated) {
                reset({
                    transferDate: stockTransferUpdated.transferDate,
                    originColumnId: stockTransferUpdated.originColumnId,
                    destinationColumnId: stockTransferUpdated.destinationColumnId,
                    shouldUpdateStockImmediately: stockTransferUpdated.shouldUpdateStockImmediately,
                    products: stockTransferUpdated.products.map(
                        (product: GetStockTransferQuery['stockTransfer']['products'][number]) => {
                            const conditionning = product.conditionning || 0;
                            const packagesPerBatch = product.packagesPerBatch || 0;
                            const quantity = product.quantity || 0;
                            return {
                                productId: product.productId,
                                quantity,
                                packages: conditionning > 0 ? round(quantity / conditionning, 2) : 0,
                                palette:
                                    conditionning > 0 && packagesPerBatch > 0
                                        ? round(quantity / conditionning / packagesPerBatch, 2)
                                        : 0,
                                fullname: product.fullname,
                                conditionning,
                                packagesPerBatch,
                                originStockAvailable: product.originStockAvailable,
                                destinationStockAvailable: product.destinationStockAvailable,
                            };
                        },
                    ),
                });
            }
            toast.warn("Le transfert de stock vient d'être mis à jour");
        });

        return () => subscription.unsubscribe();
    }, [stockTransferId, reset]);

    useBlocker(
        isDirty
            ? ({ currentLocation, nextLocation }) => {
                  if (currentLocation.pathname !== nextLocation.pathname) {
                      setPendingNavigation(nextLocation.pathname);
                      return true;
                  }
                  return false;
              }
            : false,
    );

    useEffect(() => {
        if (pendingNavigation) {
            const handleConfirm = async () => {
                const confirmed = await confirmPopup({
                    title: 'Confirmer',
                    message: 'Êtes-vous sûr de vouloir quitter la page sans sauvegarder ?',
                });
                if (confirmed) {
                    navigate(pendingNavigation);
                }
                setPendingNavigation(null);
            };
            handleConfirm();
        }
    }, [pendingNavigation, navigate]);

    if (stockTransferLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (stockTransferError || !stockTransferData) {
        throw new Error(`L'erreur suivante est survenue : ${stockTransferError}`);
    }

    const { stockTransfer: apolloStockTransferDetails } = stockTransferData;

    const { createdAt, deliveryId, delivery, state, stateHistory, updatedAt } = apolloStockTransferDetails;

    const formValues = watch();
    const originColumnId = formValues.originColumnId;
    const destinationColumnId = formValues.destinationColumnId;
    const transferDate = formValues.transferDate;

    const handleFormSubmit = handleSubmit(async (fields) => {
        const formattedProducts = fields.products.map((product) => ({
            expiryDates: [], //for now we don't modify expiry dates when creating ST manually
            productId: product.productId,
            quantity: product.quantity,
        }));
        const { data } = await updateStockTransfer({
            variables: {
                stockTransferId,
                transferDate: fields.transferDate,
                originColumnId: fields.originColumnId,
                destinationColumnId: fields.destinationColumnId,
                products: formattedProducts,
                shouldUpdateStockImmediately: fields.shouldUpdateStockImmediately,
            },
        });
        if (!data) {
            toast.error('Une erreur inconnue est survenue lors de la modification du transfert de stock');
            return;
        }
        if (!data.updateStockTransfer.success) {
            toast.error(`L'erreur suivante est survenue : "${data.updateStockTransfer.error}"`);
            return;
        }
        reset(fields);
        toast.success('Le transfert de stock a bien été modifié');
    });

    const isTransferValidated = state === StockTransferState.Validated;
    const isTransferAborted = state === StockTransferState.Aborted;
    const isReassortment = !!apolloStockTransferDetails.reassortmentFlow;
    const canAbort = !isTransferAborted && (state === StockTransferState.Created || isReassortment);

    const handleAbort = async () => {
        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message:
                isReassortment && isTransferValidated
                    ? 'Ce réassort est validé : annuler va aussi annuler les box associées et reverser les stocks préparés. Êtes-vous sûr.e ?'
                    : 'Êtes-vous sûr.e de vouloir annuler le transfert de stock ?',
        });
        if (!hasConfirmed) return;

        const { data, errors: graphqlErrors } = await abortStockTransfers({
            variables: { stockTransferIds: [stockTransferId] },
        });
        if (graphqlErrors?.length) {
            toast.error("Une erreur est survenue lors de l'annulation du transfert de stock");
            return;
        }
        const errors = data?.abortStockTransfers?.errors ?? [];
        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });
            return;
        }
        if (data?.abortStockTransfers?.stockTransfers?.length) {
            toast.success('Le transfert de stock a bien été annulé');
        }
    };

    const navigationGroup: PageActionGroup = {
        label: 'Navigation',
        items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/stock-transfers' }],
    };

    const actionGroups: PageActionGroup[] = canAbort
        ? [
              {
                  items: [
                      {
                          label: 'Annuler le transfert de stock',
                          icon: <Trash2 className="size-4" />,
                          onClick: () => {
                              void handleAbort();
                          },
                          disabled: abortLoading || isDirty,
                          variant: 'destructive',
                          dataTest: 'stock-transfer-abort',
                      },
                  ],
              },
              navigationGroup,
          ]
        : [navigationGroup];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.stockTransferDetails} />
                        </HeaderTitle>
                        {!isTransferValidated && !isTransferAborted ? (
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={() => handleFormSubmit()}
                                saveDisabled={!isDirty}
                                saveLoading={updateLoading}
                                saveTitle="Mettre à jour"
                                saveDataTest="stock-transfer-details_submit-button"
                            />
                        ) : (
                            <PageActionsMenu groups={actionGroups} />
                        )}
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <SectionColumn>
                                <InfoDetails>
                                    <DetailFormDatePicker
                                        name="transferDate"
                                        label="Date du transfert"
                                        defaultValue={transferDate}
                                        control={
                                            control as unknown as Parameters<typeof DetailFormDatePicker>[0]['control']
                                        }
                                        transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                                        transformDateToValue={(date) =>
                                            date ? formatDateAsAnniversary({ dateTime: date }) : null
                                        }
                                        isDisabled={isTransferValidated}
                                        data-test="transfer-date"
                                    />
                                    <DetailValue
                                        label="Date de création"
                                        value={getExtendedFormattedDatetime(createdAt)}
                                    />
                                    <DetailValue
                                        label="Dernière mise à jour"
                                        value={getExtendedFormattedDatetime(updatedAt)}
                                    />
                                    {deliveryId ? (
                                        <DetailLink label="Livraison associée" path="delivery" value={deliveryId} />
                                    ) : (
                                        <DetailValue label="Livraison associée" value="Pas de livraison associée" />
                                    )}
                                    <DetailValue label="Statut de livraison" value={delivery?.state || '-'} />
                                </InfoDetails>

                                {!isTransferValidated ? (
                                    <WarningText>
                                        Attention ! Les transferts de stock sont validés automatiquement tous les jours
                                        à 12h58 et à 14h58
                                    </WarningText>
                                ) : null}
                                <ColumnsSelector isDisabled={isTransferValidated} />
                                <DetailFormCheckbox
                                    name="shouldUpdateStockImmediately"
                                    label="Mettre à jour les stocks immediatement, sans création de livraison ni de RE. ⚠️⚠️⚠️ À faire que par des professionnels ⚠️⚠️⚠️"
                                />
                                <StatesSection
                                    stateHistory={stateHistory}
                                    stockTransfer={apolloStockTransferDetails}
                                    isDirty={isDirty}
                                />
                                <ProductsToTransferTable
                                    originColumnId={originColumnId}
                                    destinationColumnId={destinationColumnId}
                                    isTransferValidated={isTransferValidated}
                                    transferDate={transferDate}
                                    data-test="products-list"
                                />
                            </SectionColumn>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const WarningText = styled.div`
    color: ${colors.orange};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const InfoDetails = styled.div`
    display: flex;
    width: 100%;

    & > * {
        flex: 1;
    }
`;
